import type { Ctx } from '@milkdown/kit/ctx';
import { useInstance } from '@milkdown/react';
import { useCallback } from 'react';

/**
 * Hook to run actions against the Milkdown editor context.
 * Returns a stable function that executes the given callback with the editor ctx.
 */
export function useEditorAction() {
  const [loading, get] = useInstance();

  const action = useCallback(
    <T>(fn: (ctx: Ctx) => T): T | undefined => {
      if (loading) return;

      try {
        return get().action(fn);
      } catch (e) {
        // Context may already be torn down on unmount; swallow "not found" so cleanup can finish.
        const msg = e instanceof Error ? e.message : String(e);
        if (!msg.includes('not found')) throw e;
      }
    },
    [loading, get],
  );

  return { action, loading, getInstance: get };
}
